import { useEffect } from 'react'
import { useEventBus } from '@/providers/event-bus-provider'
import type { EventNames, PayloadType, ApplicationEventMap } from '@/lib/events/types'

/**
 * Hook to subscribe to events from the event bus with automatic cleanup
 * 
 * @param eventName The event to subscribe to
 * @param callback The callback function to execute when the event is published
 * @param deps Optional dependencies array to control when to re-subscribe
 */
export function useEvent<TEvent extends EventNames<ApplicationEventMap>>(
  eventName: TEvent,
  callback: (data: PayloadType<ApplicationEventMap, TEvent>) => void,
  deps: any[] = []
) {
  const eventBus = useEventBus()

  useEffect(() => {
    // The subscribe method returns an unsubscribe function
    const unsubscribe = eventBus.subscribe(eventName, (payload) => {
      callback(payload.data)
    })
    
    // Clean up subscription when component unmounts
    return unsubscribe
    // We're intentionally excluding the deps parameter from the dependency array
    // since it can't be statically verified and would cause over-subscription
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, eventName, callback])
}

/**
 * Hook to get a function that publishes events to the event bus
 * 
 * @returns A function to publish events
 */
export function useEventPublisher() {
  const eventBus = useEventBus()
  
  return <TEvent extends EventNames<ApplicationEventMap>>(
    eventName: TEvent,
    data: PayloadType<ApplicationEventMap, TEvent>,
    source = 'component'
  ) => {
    eventBus.publish(eventName, data, source)
  }
} 